import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

export interface DisciplinaryRecord {
  id: string;
  tenant_id: string;
  employee_id: string;
  type:
    | 'verbal_warning'
    | 'written_warning'
    | 'final_warning'
    | 'suspension'
    | 'pip'
    | 'termination';
  reason: string;
  description: string | null;
  issued_by: string | null;
  issued_at: string;
  expiry_date: string | null;
  status: 'active' | 'expired' | 'appealed' | 'overturned';
  created_by: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  resolution_notes: string | null;
}

export interface CreateDisciplinaryRecordInput {
  employee_id: string;
  type?: DisciplinaryRecord['type'];
  reason: string;
  description?: string | null;
  issued_by?: string | null;
  issued_at?: string;
  expiry_date?: string | null;
}

export interface UpdateDisciplinaryRecordInput {
  status?: DisciplinaryRecord['status'];
  description?: string | null;
  expiry_date?: string | null;
  resolved_at?: string | null;
  resolution_notes?: string | null;
}

export const disciplinaryKeys = {
  all: ['disciplinary-records'] as const,
  list: (tenantId: string) => [...disciplinaryKeys.all, 'list', tenantId] as const,
  byEmployee: (tenantId: string, employeeId: string) =>
    [...disciplinaryKeys.all, 'employee', tenantId, employeeId] as const,
} as const;

export function useDisciplinaryRecords(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: disciplinaryKeys.list(tenantId),
    queryFn: async () => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('disciplinary_records')
        // @ts-ignore dynamic
        .select('*')
        .eq('tenant_id', tenantId)
        .order('issued_at', { ascending: false });
      if (error) throw error;
      return data as DisciplinaryRecord[];
    },
    enabled: Boolean(tenantId),
  });
}

export function useCreateDisciplinaryRecord(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateDisciplinaryRecordInput) => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('disciplinary_records')
        // @ts-ignore dynamic
        .insert({ ...input, tenant_id: tenantId })
        .select()
        .single();
      if (error) throw error;
      return data as DisciplinaryRecord;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: disciplinaryKeys.list(tenantId) }),
  });
}

export function useUpdateDisciplinaryRecord(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateDisciplinaryRecordInput & { id: string }) => {
      const { data, error } = await (supabase as unknown as { from: (t: string) => any })
        .from('disciplinary_records')
        // @ts-ignore dynamic
        .update({ ...input, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .select()
        .single();
      if (error) throw error;
      return data as DisciplinaryRecord;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: disciplinaryKeys.list(tenantId) }),
  });
}

export function useDeleteDisciplinaryRecord(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as unknown as { from: (t: string) => any })
        .from('disciplinary_records')
        // @ts-ignore dynamic
        .delete()
        .eq('id', id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: disciplinaryKeys.list(tenantId) }),
  });
}
